import { MediaPicker } from '@/components/media/MediaPicker';
import type { MediaRow } from '@/routes/media/types';
import { Button, Chip, Input, Select, Spinner } from '@sjuni/ui';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { PublishingSidebar, type PublishStatus } from '@/components/editor/PublishingSidebar';
import { RichTextField } from '@/components/editor/RichTextField';
import { SlugField } from '@/components/form/SlugField';
import { TranslatableField } from '@/components/form/TranslatableField';
import { TranslationProgress } from '@/components/form/TranslationProgress';
import { useResourceList, useResourceRecord, useSaveResource } from '@/lib/api/resource';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';
import { useUnsavedChanges } from '@/lib/forms/useUnsavedChanges';

import { FacultyTagPicker } from './FacultyTagPicker';
import { TagPicker } from './TagPicker';
import type { NewsCategoryRow, NewsInput, NewsRow, TagRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §4 — the news editor.
 *
 * Everything §4 asks of a content editor is here, and two things specific to
 * this module:
 *
 *   - §3's "Submit for review". The button label comes from `PublishingSidebar`,
 *     which reads the permission; the *flag* is set here, because only the form
 *     knows what it is about to send.
 *   - §3's locked faculty selector. A Faculty Admin's article must carry their
 *     faculty tag or `NewsPolicy::update()` locks them out of the record they
 *     just wrote, so for them the picker is one pre-filled, unremovable chip.
 */
interface Fields {
  category_id: number | '';
  title: TranslatedValue;
  slug: TranslatedValue;
  excerpt: TranslatedValue;
  body: TranslatedValue;
  meta_title: TranslatedValue;
  meta_description: TranslatedValue;
  cover_media_id: number | null;
  cover: MediaRow | null;
  author_name: string;
  source_url: string;
  is_featured: boolean;
  needs_review: boolean;
  pending_review: boolean;
  status: PublishStatus;
  published_at: string | null;
  tag_ids: number[];
  faculty_ids: number[];
}

export interface NewsEditorProps {
  /** The record id, or `new`. Comes from the module's splat route. */
  recordId: string;
}

export function NewsEditor({ recordId }: NewsEditorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useEditorLocale();
  const { can, user } = useAuth();

  const isNew = recordId === 'new';
  const mayPublish = can('news.publish');

  const [formError, setFormError] = useState<string | null>(null);

  const record = useResourceRecord<NewsRow>('news', isNew ? null : Number(recordId));

  const categories = useResourceList<NewsCategoryRow>('news-categories', {
    per_page: 100,
    sort: 'sort_order',
  });

  const tags = useResourceList<TagRow>('tags', { per_page: 100 });

  const save = useSaveResource<NewsRow, NewsInput>('news');

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<Fields>({ defaultValues: emptyArticle(user?.faculty?.id ?? null) });

  // The record arrives after the first render, so the form is filled rather
  // than initialised — `reset` also clears the dirty flag, which the guard
  // below depends on.
  useEffect(() => {
    if (record.data) {
      reset(toFields(record.data.data));
    }
  }, [record.data, reset]);

  const guard = useUnsavedChanges(isDirty && !isSubmitting);

  const values = watch();

  const onSubmit = handleSubmit(async (form) => {
    setFormError(null);

    try {
      const saved = await save.mutateAsync({
        id: isNew ? undefined : Number(recordId),
        input: toInput(form, mayPublish),
      });

      navigate(`/news/${saved.data.id}`, { replace: true });
      reset(toFields(saved.data));
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(error, setError, [
        'category_id',
        'title',
        'slug',
        'excerpt',
        'body',
        'meta_title',
        'meta_description',
        'cover_media_id',
        'author_name',
        'source_url',
        'status',
        'published_at',
        'faculty_ids',
        'tag_ids',
      ]);

      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  if (!isNew && record.isPending) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-h2 font-extrabold text-primary">
            {isNew ? t('news.new') : t('news.edit')}
          </h1>

          <div className="mt-2 flex flex-wrap gap-2">
            {record.data?.data.pending_review ? (
              <Chip tone="gold">{t('editor.publishing.awaitingReview')}</Chip>
            ) : null}

            {/*
              docs/07-MIGRATION.md's flag. Shown rather than hidden because
              this editor is where a migrated row gets checked and cleared, and
              a flag nobody can see is a flag nobody clears.
            */}
            {record.data?.data.needs_review ? (
              <Chip tone="gold">{t('news.flags.needsReview')}</Chip>
            ) : null}
          </div>
        </div>

        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/news')}>
          {t('common.back')}
        </Button>
      </div>

      <FormError message={formError} />

      <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
        <div className="space-y-5">
          <TranslatableField
            label={t('news.fields.title')}
            name="title"
            value={values.title}
            onChange={(next) => setValue('title', next, { shouldDirty: true })}
            error={errors.title?.message}
          />

          <SlugField
            value={values.slug}
            onChange={(next) => setValue('slug', next, { shouldDirty: true })}
            original={record.data?.data.slug}
            published={values.status === 'published'}
            error={errors.slug?.message}
          />

          <Select
            label={t('news.fields.category')}
            hint={t('news.fields.categoryHint')}
            {...register('category_id', { valueAsNumber: true })}
            error={errors.category_id?.message}
            options={[
              { value: '', label: t('news.fields.categoryEmpty') },
              ...(categories.data?.data ?? []).map((category) => ({
                value: String(category.id),
                label: valueFor(category.name, locale),
              })),
            ]}
          />

          <TranslatableField
            label={t('news.fields.excerpt')}
            hint={t('news.fields.excerptHint')}
            name="excerpt"
            multiline
            rows={3}
            value={values.excerpt}
            onChange={(next) => setValue('excerpt', next, { shouldDirty: true })}
            required={false}
          />

          <RichTextField
            label={t('news.fields.body')}
            value={values.body}
            onChange={(next) => setValue('body', next, { shouldDirty: true })}
            error={errors.body?.message}
          />

          <TagPicker
            tags={tags.data?.data ?? []}
            selected={values.tag_ids}
            onChange={(next) => setValue('tag_ids', next, { shouldDirty: true })}
          />

          <FacultyTagPicker
            selected={values.faculty_ids}
            onChange={(next) => setValue('faculty_ids', next, { shouldDirty: true })}
            error={errors.faculty_ids?.message}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              {...register('author_name')}
              label={t('news.fields.author')}
              hint={t('news.fields.authorHint')}
              error={errors.author_name?.message}
            />

            <Input
              {...register('source_url')}
              type="url"
              label={t('news.fields.source')}
              hint={t('news.fields.sourceHint')}
              error={errors.source_url?.message}
            />

            {/*
              A raw path until §4.5's media picker lands (task #71). Editable
              rather than hidden, because the migration writes one and an editor
              who needs to clear a broken cover has no other way to.
            */}
            <MediaPicker
              label={t('news.fields.cover')}
              hint={t('news.fields.coverHint')}
              value={values.cover}
              onChange={(mediaId, media) => {
                setValue('cover_media_id', mediaId, { shouldDirty: true });
                setValue('cover', media, { shouldDirty: true });
              }}
              error={errors.cover_media_id?.message}
            />

            {/*
             * The hint is not decoration. This checkbox spent eight phases
             * doing nothing at all — the column, its index and a public
             * `?featured=1` filter existed and no page read any of them — so
             * an editor ticking it had no way to find out what it meant. It
             * pins now (`News::scopeFeaturedFirst`), and saying so here is
             * cheaper than saying it in the admin guide alone.
             */}
            <div className="self-end pb-3">
              <label className="flex items-center gap-2 text-body-sm text-primary">
                <input
                  type="checkbox"
                  {...register('is_featured')}
                  aria-describedby="is-featured-hint"
                  className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
                />
                {t('news.fields.featured')}
              </label>

              <p id="is-featured-hint" className="mt-1 text-caption text-secondary">
                {t('news.fields.featuredHint')}
              </p>
            </div>
          </div>

          {/* §5 of the frontend spec — the per-record SEO overrides. */}
          <details className="rounded-card border border-subtle p-4">
            <summary className="cursor-pointer text-body-sm font-semibold text-primary">
              {t('news.seo.title')}
            </summary>

            <div className="mt-4 space-y-5">
              <p className="text-caption text-muted">{t('news.seo.body')}</p>

              <TranslatableField
                label={t('news.fields.metaTitle')}
                name="meta_title"
                value={values.meta_title}
                onChange={(next) => setValue('meta_title', next, { shouldDirty: true })}
                required={false}
              />

              <TranslatableField
                label={t('news.fields.metaDescription')}
                name="meta_description"
                multiline
                rows={2}
                value={values.meta_description}
                onChange={(next) => setValue('meta_description', next, { shouldDirty: true })}
                required={false}
              />
            </div>
          </details>

          {record.data?.data.needs_review ? (
            <label className="flex items-center gap-2 text-body-sm text-primary">
              <input
                type="checkbox"
                // Inverted: the editor clears the flag rather than raising it,
                // which is the only direction this box is ever used in.
                checked={!values.needs_review}
                onChange={(event) =>
                  setValue('needs_review', !event.target.checked, { shouldDirty: true })
                }
                className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
              />
              {t('news.fields.checked')}
            </label>
          ) : null}
        </div>

        <div className="space-y-4">
          <PublishingSidebar
            resource="news"
            status={values.status}
            onStatusChange={(status) => setValue('status', status, { shouldDirty: true })}
            publishedAt={values.published_at}
            onPublishedAtChange={(next) => setValue('published_at', next, { shouldDirty: true })}
            updatedAt={record.data?.data.updated_at}
            updatedBy={record.data?.data.updated_by}
            pendingReview={record.data?.data.pending_review}
            saving={isSubmitting}
          />

          <TranslationProgress
            fields={{
              title: { label: t('news.fields.title'), value: values.title },
              excerpt: { label: t('news.fields.excerpt'), value: values.excerpt },
              body: { label: t('news.fields.body'), value: values.body },
            }}
          />

          {record.data ? (
            <p className="rounded-card border border-subtle p-3 text-caption text-muted">
              {t('news.views', { count: record.data.data.view_count })}
            </p>
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        open={guard.blocking}
        title={t('editor.unsaved.title')}
        body={t('editor.unsaved.body')}
        confirmLabel={t('editor.unsaved.discard')}
        reversible={false}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </form>
  );
}

function emptyArticle(ownFacultyId: number | null): Fields {
  return {
    category_id: '',
    title: {},
    slug: {},
    excerpt: {},
    body: {},
    meta_title: {},
    meta_description: {},
    cover_media_id: null,
    cover: null,
    author_name: '',
    source_url: '',
    is_featured: false,
    needs_review: false,
    pending_review: false,
    status: 'draft',
    published_at: null,
    tag_ids: [],
    /*
     * §3 — "the faculty selector is locked and pre-filled". Pre-filled here
     * rather than on submit, so a Faculty Admin sees the tag their article will
     * carry rather than discovering it afterwards.
     */
    faculty_ids: ownFacultyId === null ? [] : [ownFacultyId],
  };
}

function toFields(row: NewsRow): Fields {
  return {
    category_id: row.category_id,
    title: row.title,
    slug: row.slug,
    excerpt: row.excerpt,
    body: row.body,
    meta_title: row.meta_title,
    meta_description: row.meta_description,
    cover_media_id: row.cover_media_id ?? null,
    cover: row.cover ?? null,
    author_name: row.author_name ?? '',
    source_url: row.source_url ?? '',
    is_featured: row.is_featured,
    needs_review: row.needs_review,
    pending_review: row.pending_review,
    status: row.status,
    published_at: row.published_at,
    tag_ids: row.tags.map((tag) => tag.id),
    faculty_ids: row.faculties.map((faculty) => faculty.id),
  };
}

/**
 * §3's "Submit for review", as a payload.
 *
 * `mayPublish` decides one field. Somebody without `news.publish` saving this
 * form *is* submitting it — the sidebar has already told them so and labelled
 * the button accordingly — so the flag goes up here rather than behind a second
 * button they might not press. The server refuses a status change from them
 * either way.
 *
 * A publisher's save carries the flag through unchanged rather than clearing
 * it. Opening a submitted article to fix a typo is not the same as deciding
 * about it, and a save that quietly emptied the review queue would leave the
 * Faculty Admin waiting on a request nobody can see any more. Publishing is
 * what answers it, and the server clears it there.
 */
function toInput(form: Fields, mayPublish: boolean): NewsInput {
  return {
    category_id: Number(form.category_id),
    title: form.title,
    slug: form.slug,
    excerpt: form.excerpt,
    body: form.body,
    meta_title: form.meta_title,
    meta_description: form.meta_description,
    // Empty means "no cover", not an empty string — the column is nullable and
    // a blank path would render as a broken image rather than as no image.
    cover_media_id: form.cover_media_id,
    author_name: form.author_name.trim() === '' ? null : form.author_name.trim(),
    source_url: form.source_url.trim() === '' ? null : form.source_url.trim(),
    is_featured: form.is_featured,
    needs_review: form.needs_review,
    pending_review: mayPublish ? form.pending_review : true,
    status: form.status,
    published_at: form.published_at,
    tag_ids: form.tag_ids,
    faculty_ids: form.faculty_ids,
  };
}
